import { useEffect, useRef } from 'react';
import { Difficulty, Streak, StyledFlex } from '@tryhackme/thm-ui-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMediaQuery } from 'src/common/hooks/use-media-query';
import { BREAKPOINTS } from 'src/common/constants/breakpoints';
import { LocationState } from 'src/common/interfaces/analytics';
import { useAppDispatch } from 'src/app/hooks';
import { extendedApiSlice } from 'src/common/slices';
import { RTKQueryTag } from 'src/common/constants';
import { RoomBannerActions } from '../../room-banner/components';
import { StyledRoomDescriptionWrapper, StyledRoomHeaderWrapper, StyledRoomImage, StyledRoomActionsWrapper, StyledSideSectionCloseBtn, StyledLoading, StyledRoomTitle } from './room-header.styles';
import { RoomHeaderProps } from './room-header.types';
export const RoomHeader = ({
  streak,
  roomDetails,
  isPrivateRoom,
  isRetiredRoom,
  setRoomProgress,
  setShowVideo,
  splitScreenTabs,
  vmStartType,
  isSplitScreenOpen,
  resetToFirstTask,
  currentTask
}: RoomHeaderProps) => {
  const {
    roomCode
  } = useParams();
  if (!roomCode) throw new Error('No roomCode in url params');
  const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.TABLET}px)`);
  const isTabletAndNotSplitScreen = isTablet && !isSplitScreenOpen;
  const {
    state: historyState
  } = (useLocation() as {
    state: LocationState;
  });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const handleCloseHeader = () => {
    dispatch(extendedApiSlice.util.invalidateTags([RTKQueryTag.PathsCurrent]));
    navigate(historyState?.pageReferrer ?? '/dashboard');
  };
  const isLoading = !roomDetails;
  const roomHeaderRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (currentTask?.taskNo) {
      roomHeaderRef.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [currentTask?.taskNo]);
  return <StyledRoomHeaderWrapper $isTabletAndNotSplitScreen={isTabletAndNotSplitScreen} ref={roomHeaderRef} data-testid="room-header" data-sentry-element="StyledRoomHeaderWrapper" data-sentry-component="RoomHeader" data-sentry-source-file="room-header.tsx">
      <StyledFlex style={{
      flex: 1
    }} data-sentry-element="StyledFlex" data-sentry-source-file="room-header.tsx">
        {isLoading ? <StyledLoading width="35.6rem" data-testid="loading" /> : <StyledRoomDescriptionWrapper>
            <StyledRoomImage src={roomDetails.imageURL} errorSrc="https://assets.tryhackme.com/img/banners/default_tryhackme.png" aria-label="Room Icon" />
            <StyledFlex gap="1.6rem">
              <StyledRoomTitle>{roomDetails.title}</StyledRoomTitle>
              <Difficulty difficulty={roomDetails.difficulty} hasIcon isDarkVersion />
            </StyledFlex>
          </StyledRoomDescriptionWrapper>}
        {isLoading ? <StyledLoading width="50.6rem" /> : <StyledRoomActionsWrapper>
            <RoomBannerActions roomDetails={roomDetails} isPrivateOrRetiredRoom={isPrivateRoom || isRetiredRoom} isPrivateRoom={isPrivateRoom} isRetiredRoom={isRetiredRoom} setRoomProgress={setRoomProgress} setShowVideo={setShowVideo} splitScreenTabs={splitScreenTabs} vmStartType={vmStartType} resetToFirstTask={resetToFirstTask} isTaskBreakdownSupported />

            {streak && <Streak {...streak} />}
          </StyledRoomActionsWrapper>}
      </StyledFlex>
      <StyledSideSectionCloseBtn onClick={handleCloseHeader} aria-label="close" data-sentry-element="StyledSideSectionCloseBtn" data-sentry-source-file="room-header.tsx">
        <FontAwesomeIcon icon={faXmark} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="room-header.tsx" />
      </StyledSideSectionCloseBtn>
    </StyledRoomHeaderWrapper>;
};